import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Form,
  Label,
  FormFeedback,
  Input,
  Spinner,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Accordion,
  AccordionItem,
  Collapse,
} from "reactstrap";
import classnames from "classnames";
import Flatpickr from "react-flatpickr";
import SelectContractor from "../../Contracts/SelectContractor";
import PSEACoreStandardItem from "./PSEACoreStandardItem";
import AssessingAgencyField from "./AssessingAgencyField";
import { PSEA_CORE_STANDARDS } from "./pseaCoreStandards";
import { buildPseaTitle, contractorNameFromLabel } from "./pseaTitle";
import {
  computePseaScore,
  countAnswered,
  isStandardScored,
  pseaAnswer,
  pseaRatingColor,
  pseaRatingFromScore,
} from "./pseaScoring";
import { standardsMissingDocuments } from "./pseaValidation";

const PSEAAssessmentForm: React.FC<{
  validation: any;
  loading: boolean;
  isUpdate?: boolean;
  disabled?: boolean;
  onCancel: () => void;
}> = ({ validation, loading, isUpdate, disabled, onCancel }) => {
  // Multiple standards can stay open at once so evidence can be compared while
  // filling the form in.
  const [openStandards, setOpenStandards] = useState<string[]>(["std-1"]);

  const toggleStandard = (id: string) => {
    setOpenStandards((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const allIds = PSEA_CORE_STANDARDS.map((s) => `std-${s.number}`);
  const allOpen = openStandards.length === allIds.length;

  const toggleAll = () => setOpenStandards(allOpen ? [] : allIds);

  const documentCount = (index: number) =>
    (validation.values.coreStandards?.[index]?.selectedFiles || []).length;

  // One point per standard marked as met. The backend recomputes this on
  // save; showing it here keeps the rating visible while the form is filled in.
  const coreStandards = validation.values.coreStandards || [];
  const score = computePseaScore(coreStandards);
  const answered = countAnswered(coreStandards);
  const rating = pseaRatingFromScore(score);
  const ratingColor = pseaRatingColor(rating);
  const coverage = Math.round((score / PSEA_CORE_STANDARDS.length) * 100);
  const answeredAll = answered === PSEA_CORE_STANDARDS.length;

  // Standards answered "Yes" with no supporting document attached. Only shown
  // once the form has been submitted, matching the per-standard messages.
  const missingDocs = standardsMissingDocuments(validation.errors);

  /**
   * Everything blocking the submit, gathered so the action bar can list it in
   * one place. The fields sit far apart on a long form — the justification is
   * several cards below the standards — so an inline message on its own is
   * easy to miss from wherever the submit button happens to be.
   */
  const blockingErrors = useMemo(() => {
    if (validation.submitCount === 0) return [];
    const list: { message: string; fieldId?: string }[] = [];

    // Yup reports the contractor error against the nested `value`.
    const contractor: any = validation.errors.contractor;
    const contractorMessage =
      typeof contractor === "string" ? contractor : contractor?.value;
    if (contractorMessage) {
      list.push({ message: contractorMessage, fieldId: "contractor" });
    }

    if (typeof validation.errors.justification === "string") {
      list.push({
        message: validation.errors.justification,
        fieldId: "justification",
      });
    }

    if (missingDocs.length > 0) {
      const titles = missingDocs
        .map((index: number) => PSEA_CORE_STANDARDS[index].title)
        .join(", ");
      list.push({
        message: `${missingDocs.length} standard${
          missingDocs.length === 1 ? "" : "s"
        } marked “Yes” ${
          missingDocs.length === 1 ? "needs" : "need"
        } a supporting document: ${titles}.`,
      });
    }

    return list;
  }, [validation.submitCount, validation.errors, missingDocs]);

  /** Jumps to the offending field so the fix is one click away. */
  const focusField = (fieldId: string) => {
    const el = document.getElementById(fieldId);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.focus({ preventScroll: true });
  };

  /**
   * Most standards sit collapsed, so a failed submit would otherwise hide the
   * reason. Opening the offending ones puts the error on screen.
   *
   * Keyed on the offending standards as well as the submit count: Formik bumps
   * `submitCount` before validation has populated `errors`, so watching the
   * count alone runs this while there is still nothing to open.
   */
  const missingDocsKey = missingDocs.join(",");
  useEffect(() => {
    if (validation.submitCount === 0 || !missingDocsKey) return;
    const ids = missingDocsKey
      .split(",")
      .map((index) => `std-${PSEA_CORE_STANDARDS[Number(index)].number}`);
    setOpenStandards((prev) => Array.from(new Set([...prev, ...ids])));
  }, [validation.submitCount, missingDocsKey]);

  /**
   * The action bar sits after this sentinel, so an off-screen sentinel means
   * the bar is pinned with form content still scrolling beneath it. Only then
   * does it get the raised, detached treatment; once you reach the end of the
   * form it settles back into the page.
   */
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [isPinned, setIsPinned] = useState(false);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || typeof IntersectionObserver === "undefined") return;

    const observer = new IntersectionObserver(
      ([entry]) => setIsPinned(!entry.isIntersecting),
      { threshold: 0 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const generatedTitle =
    buildPseaTitle(
      contractorNameFromLabel(validation.values.contractor?.label)
    ) ||
    validation.values.title ||
    "";

  return (
    <Form
      className="tablelist-form"
      onSubmit={(e) => {
        e.preventDefault();
        validation.handleSubmit();
        return false;
      }}
    >
      {/* Section 1: Overall Details */}
      <Card>
        <CardHeader>
          <h5 className="card-title mb-0">Overall Details</h5>
        </CardHeader>
        <CardBody>
          <Row>
            <Col md={6} className="mb-3">
              {isUpdate ? (
                <React.Fragment>
                  <Label htmlFor="contractor-readonly" className="form-label">
                    Contractor
                  </Label>
                  <Input
                    id="contractor-readonly"
                    className="form-control"
                    type="text"
                    disabled
                    value={validation.values.contractor?.label || ""}
                  />
                  <small className="text-muted">
                    The contractor cannot be changed after the assessment is
                    created.
                  </small>
                </React.Fragment>
              ) : (
                <SelectContractor validation={validation} />
              )}
            </Col>
            <Col md={6} className="mb-3">
              <Label htmlFor="title" className="form-label">
                Title
              </Label>
              <Input
                name="title"
                id="title"
                className="form-control"
                type="text"
                disabled
                placeholder="Auto-generated from the selected contractor"
                value={generatedTitle}
              />
              <small className="text-muted">
                The title is generated automatically based on the selected
                contractor.
              </small>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* Section 2: PSEA Information (8 Core Standards) */}
      <Card>
        <CardHeader>
          <div className="d-flex align-items-start flex-wrap gap-2">
            <div className="flex-grow-1">
              <h5 className="card-title mb-1">PSEA Information</h5>
              <p className="text-muted mb-0">
                For each of the 8 core standards, indicate whether the standard
                is met. Where it is, record the evidence and attach supporting
                documents. Each standard met scores one point, as does a
                standard marked not applicable.
              </p>
            </div>
            <button
              type="button"
              className="btn btn-soft-secondary btn-sm d-inline-flex align-items-center gap-1"
              onClick={toggleAll}
            >
              <i
                className={
                  allOpen
                    ? "ri-contract-up-down-line"
                    : "ri-expand-up-down-line"
                }
              ></i>
              {allOpen ? "Collapse all" : "Expand all"}
            </button>
          </div>

          <div className="mt-3">
            <div className="d-flex justify-content-between align-items-center fs-12 mb-1">
              <span className="text-muted">Standards met</span>
              <span className="fw-semibold">
                {score} of {PSEA_CORE_STANDARDS.length}
                {!answeredAll ? (
                  <span className="text-muted fw-normal ms-1">
                    ({PSEA_CORE_STANDARDS.length - answered} unanswered)
                  </span>
                ) : null}
              </span>
            </div>
            <div className="progress" style={{ height: "6px" }}>
              <div
                className={`progress-bar bg-${ratingColor}`}
                role="progressbar"
                style={{ width: `${coverage}%` }}
                aria-valuenow={coverage}
                aria-valuemin={0}
                aria-valuemax={100}
              ></div>
            </div>
          </div>
        </CardHeader>
        <CardBody>
          <Accordion open={openStandards} toggle={toggleStandard}>
            {PSEA_CORE_STANDARDS.map((standard, index) => {
              const id = `std-${standard.number}`;
              const docs = documentCount(index);
              const isOpen = openStandards.includes(id);
              const entry = coreStandards?.[index];
              const answer = pseaAnswer(entry);
              const scored = isStandardScored(entry);
              return (
                <AccordionItem key={id}>
                  <h2 className="accordion-header">
                    <button
                      type="button"
                      className={classnames("accordion-button", {
                        collapsed: !isOpen,
                      })}
                      onClick={() => toggleStandard(id)}
                    >
                      <span className="d-flex align-items-center gap-2 flex-grow-1 flex-wrap">
                        <span
                          className={classnames(
                            "psea-standard-number avatar-xs d-flex align-items-center justify-content-center rounded-circle fw-bold fs-12 flex-shrink-0",
                            scored
                              ? "bg-primary text-white"
                              : "bg-body text-muted border"
                          )}
                        >
                          {standard.number}
                        </span>
                        <span className="fw-semibold">{standard.title}</span>
                        {answer === "yes" ? (
                          <span className="badge bg-primary-subtle text-primary">
                            Yes &middot; 1 point
                          </span>
                        ) : answer === "na" ? (
                          <span className="badge bg-primary-subtle text-primary">
                            {standard.number === 8
                              ? "No Allegations"
                              : "Not applicable"}{" "}
                            &middot; 1 point
                          </span>
                        ) : answer === "no" ? (
                          <span className="badge bg-light text-muted border">
                            No &middot; 0 points
                          </span>
                        ) : (
                          <span className="badge bg-light text-muted border">
                            Not answered
                          </span>
                        )}
                        {answer === "yes" && docs > 0 ? (
                          <span className="badge bg-light text-muted border">
                            <i className="ri-attachment-2 align-bottom me-1"></i>
                            {docs}
                          </span>
                        ) : null}
                        {validation.submitCount > 0 &&
                        missingDocs.includes(index) ? (
                          <span className="badge bg-danger-subtle text-danger">
                            <i className="ri-error-warning-line align-bottom me-1"></i>
                            Document required
                          </span>
                        ) : null}
                      </span>
                    </button>
                  </h2>
                  <Collapse isOpen={isOpen} className="accordion-collapse">
                    <div className="accordion-body">
                      <PSEACoreStandardItem
                        standard={standard}
                        index={index}
                        validation={validation}
                      />
                    </div>
                  </Collapse>
                </AccordionItem>
              );
            })}
          </Accordion>
        </CardBody>
      </Card>

      {/* Section 3: Assessment Details */}
      <Card>
        <CardHeader>
          <h5 className="card-title mb-1">Overall Rating</h5>
          <p className="text-muted mb-0">
            Calculated automatically from the 8 core standards &mdash; one point
            for each standard met or marked not applicable.
          </p>
        </CardHeader>
        <CardBody>
          <Row className="g-3">
            <Col md={4}>
              <div className="text-center p-3 border border-dashed rounded h-100 d-flex flex-column justify-content-center">
                <p className="text-uppercase text-muted fw-medium fs-12 mb-1">
                  Score
                </p>
                <h2 className={`mb-0 text-${ratingColor}`}>
                  {score}
                  <span className="text-muted fs-16">
                    {" "}
                    / {PSEA_CORE_STANDARDS.length}
                  </span>
                </h2>
              </div>
            </Col>
            <Col md={4}>
              <div className="text-center p-3 border border-dashed rounded h-100 d-flex flex-column justify-content-center">
                <p className="text-uppercase text-muted fw-medium fs-12 mb-2">
                  Overall Rating
                </p>
                <span
                  className={`badge bg-${ratingColor}-subtle text-${ratingColor} fs-14 px-3 py-2`}
                >
                  {rating}
                </span>
              </div>
            </Col>
            <Col md={4}>
              <div className="p-3 border border-dashed rounded h-100">
                <p className="text-uppercase text-muted fw-medium fs-12 mb-2">
                  Rating bands
                </p>
                <ul className="list-unstyled mb-0 fs-12">
                  <li className="d-flex justify-content-between">
                    <span className="text-success">Full Capacity</span>
                    <span className="text-muted">8</span>
                  </li>
                  <li className="d-flex justify-content-between">
                    <span className="text-warning">Medium Capacity</span>
                    <span className="text-muted">6 &ndash; 7</span>
                  </li>
                  <li className="d-flex justify-content-between">
                    <span className="text-danger">Low Capacity</span>
                    <span className="text-muted">0 &ndash; 5</span>
                  </li>
                </ul>
              </div>
            </Col>
          </Row>

          {!answeredAll ? (
            <Alert color="warning" className="mt-3 mb-0 material-shadow">
              <i className="ri-information-line align-bottom me-1"></i>
              {PSEA_CORE_STANDARDS.length - answered} standard
              {PSEA_CORE_STANDARDS.length - answered === 1
                ? " is"
                : "s are"}{" "}
              not yet answered. Unanswered standards score zero, so the rating
              shown may change once they are completed.
            </Alert>
          ) : null}
        </CardBody>
      </Card>

      {/* Section 4: Completed By */}
      <Card>
        <CardHeader>
          <h5 className="card-title mb-1">Completed By</h5>
          <p className="text-muted mb-0">
            Who completed this assessment, on whose behalf, and when — these
            details are captured in the assessment history.
          </p>
        </CardHeader>
        <CardBody>
          <Row>
            <Col md={4} className="mb-3">
              <Label htmlFor="enteredByName" className="form-label">
                Name
              </Label>
              <Input
                name="enteredByName"
                id="enteredByName"
                className="form-control"
                type="text"
                disabled
                value={validation.values.enteredByName || ""}
              />
            </Col>
            <Col md={4} className="mb-3">
              <Label htmlFor="enteredByEmail" className="form-label">
                Email
              </Label>
              <Input
                name="enteredByEmail"
                id="enteredByEmail"
                className="form-control"
                type="text"
                disabled
                value={validation.values.enteredByEmail || ""}
              />
            </Col>
            <Col md={4}>
              <AssessingAgencyField validation={validation} />
            </Col>
          </Row>
          <Row>
            <Col md={4} className="mb-3">
              <Label htmlFor="dateOfAssessment" className="form-label">
                Date of Assessment
              </Label>
              {/* @ts-ignore */}
              <Flatpickr
                name="dateOfAssessment"
                id="dateOfAssessment"
                className={
                  validation.touched.dateOfAssessment &&
                  validation.errors.dateOfAssessment
                    ? "is-invalid form-control"
                    : "form-control"
                }
                placeholder="Select date of assessment"
                options={{
                  enableTime: false,
                  altFormat: "d M, Y",
                  dateFormat: "d M, Y",
                }}
                onChange={(e) => {
                  validation.setFieldValue(
                    "dateOfAssessment",
                    e[0] ? e[0] : null
                  );
                }}
                value={validation.values.dateOfAssessment || ""}
              />
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* Section 5: Justification (updates only) */}
      {isUpdate ? (
        <Card>
          <CardHeader>
            <h5 className="card-title mb-1">
              Justification for Update <span className="text-danger">*</span>
            </h5>
            <p className="text-muted mb-0">
              Explain why this assessment is being updated. This is recorded
              against the update in the assessment history.
            </p>
          </CardHeader>
          <CardBody>
            <Input
              name="justification"
              id="justification"
              className="form-control"
              type="textarea"
              rows={4}
              placeholder="Enter the reason for updating this assessment"
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.justification || ""}
              invalid={
                validation.touched.justification &&
                validation.errors.justification
                  ? true
                  : false
              }
            />
            {validation.touched.justification &&
            validation.errors.justification ? (
              <FormFeedback type="invalid">
                {validation.errors.justification}
              </FormFeedback>
            ) : null}
          </CardBody>
        </Card>
      ) : null}

      {/*
        The form runs long (8 core standards plus five sections), so the
        actions stay reachable instead of living only at the very bottom.
      */}
      <Card
        className={classnames("position-sticky mb-3", {
          "shadow-lg border": isPinned,
        })}
        style={{
          bottom: "1rem",
          zIndex: 5,
          transition: "box-shadow .25s ease",
        }}
      >
        {/* Fades the form content out as it slides beneath the pinned bar. */}
        {isPinned ? (
          <div
            aria-hidden="true"
            className="position-absolute start-0 end-0"
            style={{
              top: "-1.5rem",
              height: "1.5rem",
              background:
                "linear-gradient(to bottom, rgba(0,0,0,0), rgba(0,0,0,.07))",
              pointerEvents: "none",
            }}
          ></div>
        ) : null}

        <CardBody className="py-3">
          {blockingErrors.length > 0 ? (
            <Alert
              color="danger"
              className="mb-3 material-shadow"
              role="alert"
              aria-live="polite"
            >
              <div className="d-flex gap-2">
                <i className="ri-error-warning-line fs-16 flex-shrink-0"></i>
                <div className="flex-grow-1">
                  <p className="fw-semibold mb-1">
                    {isUpdate
                      ? "This assessment could not be updated:"
                      : "This assessment could not be saved:"}
                  </p>
                  <ul className="mb-0 ps-3">
                    {blockingErrors.map((error, i) => (
                      <li key={i} className="fs-13">
                        {error.fieldId ? (
                          <button
                            type="button"
                            className="btn btn-link p-0 align-baseline fs-13 text-start psea-error-link"
                            onClick={() => focusField(error.fieldId as string)}
                          >
                            {error.message}
                          </button>
                        ) : (
                          error.message
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </Alert>
          ) : null}
          <div className="d-flex align-items-center flex-wrap gap-2">
            <span className="text-muted fs-13 flex-grow-1 d-flex align-items-center gap-2">
              {isPinned ? (
                <i className="ri-arrow-down-line text-primary"></i>
              ) : null}
              <span className="d-flex align-items-center gap-2 flex-wrap">
                <span>
                  {score} of {PSEA_CORE_STANDARDS.length} core standards met.
                </span>
                <span
                  className={`badge bg-${ratingColor}-subtle text-${ratingColor} fs-13`}
                >
                  {rating}
                </span>
                {!answeredAll ? (
                  <span className="text-muted">
                    {PSEA_CORE_STANDARDS.length - answered} still unanswered.
                  </span>
                ) : null}
              </span>
            </span>
            <button type="button" className="btn btn-light" onClick={onCancel}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || disabled}
            >
              {loading && (
                <Spinner
                  color="light"
                  size="sm"
                  style={{ marginRight: "8px", marginBottom: "-1px" }}
                />
              )}
              {isUpdate ? "Update PSEA Assessment" : "Save PSEA Assessment"}
            </button>
          </div>
        </CardBody>
      </Card>

      {/* Sentinel: visible only once the form has been scrolled to the end. */}
      <div ref={sentinelRef} aria-hidden="true" style={{ height: "1.5rem" }} />
    </Form>
  );
};

export default PSEAAssessmentForm;
